import {useState} from "react";
import {useMessages} from "i18n";
import {useDevCodeAutofill} from "login/useDevCodeAutofill";
import {createsAccount, objectionAbove, termsLineAbove} from "login/loginViews";
import {SignUpTermsLine} from "login/SignUpTermsLine";
import type {SignupObjection} from "marketing/useSignupObjection";
import {CodeInput, Copy, ErrorLine, Objection, QuietLink, Root, Title, VerifyButton} from "./VerificationStep.styles";

/**
 * Verification code entry as an INLINE create-card step (ui#132, reworking ui#131's overlay per
 * owner feedback): when submit yields PENDING, this step replaces the transfer panel inside the
 * card — part of the create → verify → progress → success flow, styled like its siblings
 * (ProgressCard/SuccessCard family: card surface, theme tokens). Dumb component — the state
 * machine stays in the passwordless login adapter.
 *
 * Verify confirms the same code the login modal's code view does, so it creates the visitor's account.
 * Above it, in this order: the marketing objection box (kitecorp/transferify-ui#3503), a sentence saying
 * that Verify creates a free account (kitecorp/transferify-ui#3504), and the sign-up Terms line
 * (kitecorp/transferify-ui#3482), all placed by `login/loginViews`. The sentence stands directly above the
 * Terms line so it reads as that line's lead-in: a visitor who came to send files has no other reason to
 * think "By creating a free account" applies to them.
 */

/** The account sentence's and the Terms line's ids, which the Verify button names as its description. */
const ACCOUNT_NOTICE_ID = "send-flow-account-notice";
const TERMS_LINE_ID = "send-flow-sign-up-terms";
const VERIFY_CREATES_ACCOUNT = createsAccount("sendFlowCode", "submit");
const OBJECTION_ABOVE = objectionAbove("sendFlowCode");
const TERMS_ABOVE = termsLineAbove("sendFlowCode");

type VerificationStepProps = {
	/** The address the code was sent to. */
	email: string;
	/** When the latest code was asked for, the first send or a resend (`useEmailCodeLogin`). */
	requestedAt: number | null;
	/** Truthful ACCEPTED/RETRY_LATER sentence, or null for an unexpected response. */
	deliveryMessage: string | null;
	/** Server-provided error copy for the last attempt, or null. */
	error: string | null;
	/** True while a confirmation round-trip is in flight. */
	busy: boolean;
	/** Domain-derived request gate; confirmation remains independent. */
	resendDisabled: boolean;
	onConfirm: (code: string) => void;
	onResend: () => void;
	/** "Use a different email" — takes the gate down and returns to the form. */
	onDismiss: () => void;
	/** The marketing objection box's state; Verify's press records the tick (`CreateFlow`). */
	objection: SignupObjection;
};

export default function VerificationStep({
	email,
	requestedAt,
	deliveryMessage,
	error,
	busy,
	resendDisabled,
	onConfirm,
	onResend,
	onDismiss,
	objection,
}: VerificationStepProps) {
	const {sender} = useMessages();
	const [code, setCode] = useState("");
	// Local-stack convenience only (ui#2387), as in the login modal: fills the code the platform inbox received
	// since the latest request. A signed-out send or request signs in here, not through the modal. Compiled out
	// of builds.
	useDevCodeAutofill(true, email, requestedAt, code, setCode);
	const ready = code.length === 6 && !busy;
	return <Root>
		<Title>{sender.verification.title}</Title>
		{deliveryMessage !== null && <Copy aria-live="off">{deliveryMessage}</Copy>}
		<CodeInput
			value={code}
			onChange={event => setCode(event.target.value.toUpperCase().replace(/\s/g, "").slice(0, 6))}
			onKeyDown={event => {
				if (event.key === "Enter" && ready) {
					onConfirm(code);
				}
			}}
			placeholder={sender.verification.placeholder}
			aria-label={sender.verification.placeholder}
			autoFocus
			spellCheck={false}
			autoComplete="one-time-code"
			inputMode="text"
		/>
		{error !== null && <ErrorLine role="alert">{error}</ErrorLine>}
		{OBJECTION_ABOVE === "submit" && <Objection objection={objection}/>}
		{VERIFY_CREATES_ACCOUNT && (
			<Copy id={ACCOUNT_NOTICE_ID}>{sender.verification.accountNotice(sender.verification.verify)}</Copy>
		)}
		{TERMS_ABOVE === "submit" && <SignUpTermsLine id={TERMS_LINE_ID}/>}
		<VerifyButton type="button" disabled={!ready} onClick={() => onConfirm(code)}
			aria-describedby={VERIFY_CREATES_ACCOUNT ? `${ACCOUNT_NOTICE_ID} ${TERMS_LINE_ID}` : undefined}>
			{sender.verification.verify}
		</VerifyButton>
		<QuietLink type="button" onClick={onResend} disabled={resendDisabled}>{sender.verification.resend}</QuietLink>
		<QuietLink type="button" onClick={onDismiss} disabled={busy}>{sender.verification.changeEmail}</QuietLink>
	</Root>;
}
